import { A365BackendModel as Model } from '../a365.backendmodel';
import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { ModelConstants } from '../model-constants';
import { convertKeyToLowerCamelCase, convertKeyToUpperCamelCase } from '../../shared/utils/string.util';
import { TreeNamedResAndNameUtils } from '../../shared/utils/tree-named-naming.util';
import { SharedModel } from '../sharedmodel';
import { Relationship } from '../relationship/relationship';
import { IColumnRelationshipInformation, IRelationship, RelationshipType } from '../relationship/interface';
import { IExecuteChangeActionType } from '../database/databaseEntity';
import { GlobalVariables } from '../../constants/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Relationship),
    adapts: {
        name: '*',
        properties: '*'
    }
})
/* eslint-disable @typescript-eslint/no-explicit-any */
export class RelationshipAdapter extends NamedAdapter<Relationship, Model.RelationshipResource> {
    /**
     * If publish or forArmTemplate is true, return payload used in executeChangeWithValidation; otherwise, return the resource
     */
    protected _toResource(relationship: Relationship, isPublish: boolean = false, forArmTemplate: boolean = false): { [key in string]: any } {
        const realName = this._correctRelationshipName(relationship);

        if (!isPublish && !forArmTemplate) {
            const sourceContent = this._generatePureNewRelationshipEntity(relationship);
            return {
                name: realName,
                properties: sourceContent,
                type: 'RELATIONSHIP'
            };
        }

        // Generate payload for executeChangeWithValidation
        const payload = this._generatePayload(relationship, forArmTemplate ? 'CREATE' : undefined);

        return {
            name: realName,
            databaseName: relationship.parentInfo ? relationship.parentInfo.resourceName : undefined,
            payload
        };
    }

    protected _toModel(relationshipResource: Model.RelationshipResource, relationship?: Relationship): Relationship {
        if (!relationship) {
            relationship = Relationship.MODEL.create<Relationship>();
        }

        // set basic info
        const relationshipData = this._convertToRelationship(relationshipResource);
        const relationshipContent = relationshipData.relationship;
        const parentName = relationshipContent.namespace ? relationshipContent.namespace.databaseName : undefined;
        const parentInfo = this._getParentInfo(parentName);
        relationship.name = TreeNamedResAndNameUtils.correctName(parentName, relationshipResource.name); // different databases could have relationship with the same name
        relationship.description = relationshipContent.description;
        relationship.parentInfo = parentInfo;
        relationship.content = relationshipContent;
        // store the original contnet
        relationship.rawContentInJson = JSON.stringify(relationshipResource.properties);
        relationship.mDEntity = {
            name: relationshipResource.name, // real name of the relationship
            objectVersion: relationshipContent.objectVersion,
            objectId: relationshipContent.objectId
        };

        if (relationshipData.origin?.instanceId) {
            relationship.originInstanceId = relationshipData.origin.instanceId;
        }

        return relationship;
    }

    private _convertToRelationship(relationshipResource: Model.RelationshipResource): { relationship: IRelationship, origin: { type: string, instanceId: string } } {
        const rawContent = convertKeyToLowerCamelCase(relationshipResource.properties);
        const columnRelationshipInformations: IColumnRelationshipInformation[] = [];
        if (rawContent.columnRelationshipInformations) {
            rawContent.columnRelationshipInformations.forEach((relationshipInfo: IColumnRelationshipInformation) => {
                const item: IColumnRelationshipInformation = {
                    fromColumnName: relationshipInfo.fromColumnName,
                    toColumnName: relationshipInfo.toColumnName
                };
                columnRelationshipInformations.push(item);
            });
        }

        // Handle properties
        const properties = rawContent.properties;
        const derivedModelRelationshipInfoInString = properties ? properties.derivedModelRelationshipInfo : undefined;
        const rawDerivedModelRelationshipInfo = convertKeyToLowerCamelCase(derivedModelRelationshipInfoInString ? JSON.parse(derivedModelRelationshipInfoInString) : undefined);
        const derivedModelRelationshipInfo = rawDerivedModelRelationshipInfo ? {
            baseRelationship: rawDerivedModelRelationshipInfo.baseRelationship
        } : undefined;

        const relationship: IRelationship = {
            name: rawContent.name ? rawContent.name : relationshipResource.name,
            description: rawContent.description,
            objectId: rawContent.objectId,
            objectVersion: rawContent.objectVersion,
            namespace: {
                ...rawContent.namespace
            },
            fromTableName: rawContent.fromTableName,
            toTableName: rawContent.toTableName,
            columnRelationshipInformations,
            relationshipType: rawContent.relationshipType ?? RelationshipType.ONETOONE,
            properties: {
                derivedModelRelationshipInfo
            }
        };

        return { relationship, origin: rawContent.origin };
    }

    private _getParentInfo(databaseName: string): SharedModel.IParentInfo {
        return {
            resourceType: ModelConstants.database,
            resourceName: databaseName
        };
    }

    private _generatePayload(relationship: Relationship, actionType?: IExecuteChangeActionType): { [key in string]: any } {
        actionType = 'CREATE';

        if (actionType) {
            const newRelationshipEntity = this._generatePureNewRelationshipEntity(relationship);
            const oldRelationshipEntity = actionType !== 'CREATE' ? this._generatePureOldRelationshipEntity(relationship) : null;
            const entity = {
                ActionType: actionType,
                OldEntity: oldRelationshipEntity,
                NewEntity: newRelationshipEntity,
                Source: {
                    Type: 'SPARK'
                }
            };

            return entity;
        }
        return null;
    }

    /**
     * Generate relationship entity which is used as newEntity in executeChange
     */
    private _generatePureNewRelationshipEntity(relationship: Relationship): { [key in string]: any } {
        const originalContent = convertKeyToUpperCamelCase(relationship.rawContentInJson ? JSON.parse(relationship.rawContentInJson) : {});
        const columnRelationshipInfos = relationship.content.columnRelationshipInformations.map((item) => convertKeyToUpperCamelCase(item));
        const derivedModelRelationshipInfo = relationship.content && relationship.content.properties ? relationship.content.properties.derivedModelRelationshipInfo : undefined;
        const properties: any = derivedModelRelationshipInfo ? {
            DerivedModelRelationshipInfo: JSON.stringify(derivedModelRelationshipInfo)
        } : {};

        const entityProperties = {
            ...originalContent,
            Namespace: {
                ...originalContent.Namespace,
                DatabaseName: relationship.parentInfo.resourceName
            },
            Description: relationship.description,
            Origin: {
                Type: 'SPARK'
            },
            FromTableName: relationship.content.fromTableName,
            ToTableName: relationship.content.toTableName,
            ColumnRelationshipInformations: columnRelationshipInfos,
            RelationshipType: relationship.content.relationshipType,
            Properties: {
                ...originalContent.Properties,
                ...properties
            }
        };

        if (relationship.originInstanceId) {
            (entityProperties.Origin as any).InstanceId = relationship.originInstanceId;
        }

        const coreProperties = {
            Name: relationship.content ? relationship.content.name : relationship.name,
            EntityType: 'RELATIONSHIP',
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };


        return entity;
    }

    /**
     * Generate relationship entity which is used as oldEntity in executeChange
     * The entity would only contain necessary attribute instead of all
     */
    private _generatePureOldRelationshipEntity(relationship: Relationship): { [key in string]: any } {
        const entityProperties = {
            Namespace: {
                DatabaseName: relationship.parentInfo.resourceName
            },
            Origin: {
                Type: 'SPARK'
            },
            ObjectId: relationship.mDEntity ? relationship.mDEntity.objectId : undefined,
            ObjectVersion: relationship.mDEntity ? relationship.mDEntity.objectVersion : undefined
        };

        const coreProperties = {
            Name: relationship.content ? relationship.content.name : relationship.name,
            EntityType: 'RELATIONSHIP',
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };

        return entity;
    }

    private _correctRelationshipName(relationship: Relationship): string {
        const nameDict = TreeNamedResAndNameUtils.extractNameAndParentName(relationship.name);
        return nameDict.name;
    }
}
